import {
  Avatar,
  Chip,
  Icon,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {Button} from '@payactive/app-common';
import React, {ReactElement, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PageHeader from '../../../components/ui/blocks/PageHeader';
import {AlertContext} from '../../../components/contexts/WithAlertContext';
import CreateApiKeyDialog from './CreateApiKeyDialog';
import RevokeApiKeyDialog from './RevokeApiKeyDialog';
import ApiKeyService from '../../../services/ApiKeyService';
import {ApiKeyResponse} from '../../../types/ApiKey/ApiKey';

const useStyles = makeStyles((theme) => ({
  even: {
    border: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  odd: {
    border: 'none',
    backgroundColor: theme.palette.background.default,
  },
  avatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.secondary.main,
  },
}));

export default function ApiKeys(): ReactElement {
  const alerting = useContext(AlertContext);
  const classes = useStyles();

  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);

  const [revokeApiKey, setRevokeApiKey] = useState<ApiKeyResponse | null>(null);

  const { t } = useTranslation(['developer', 'global']);

  useEffect(() => {
    updateApiKeys();
  }, []);

  const updateApiKeys = (): void => {
    ApiKeyService.listApiKeys().then((apiKeys) => {
      setApiKeys(apiKeys);
    });
  };

  const close = (): void => {
    setShowCreateDialog(false);
    updateApiKeys();
  };

  const handleRevokeKey = (apiKey: ApiKeyResponse): void => {
    if (apiKey.id) {
      ApiKeyService.revokeApiKey(apiKey.id).then((res) => {
        if (res) {
          updateApiKeys();
          alerting.sendAlert('success', t('api key deleted'));
        }
        setRevokeApiKey(null);
      });
    }
  };

  return (
    <React.Fragment>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <PageHeader title={t('apikeys')} />
        <Button onClick={() => setShowCreateDialog(true)} label={t('create api key')} name="create" disabled={false} loading={false} />
      </Grid>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('apikeyname')}</TableCell>
              <TableCell>{t('prefix')}</TableCell>
              <TableCell>{t('note')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('createdAt')}</TableCell>
              <TableCell>{t('expiresAt')}</TableCell>
              <TableCell>{t('revokedAt')}</TableCell>
              <TableCell align="left"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiKeys.map((apiKey, index) => (
              <TableRow key={apiKey.id || index} hover className={index % 2 === 0 ? classes.even : classes.odd}>
                <TableCell>
                  {apiKey.name}
                  {apiKey.legacy && (
                    <Chip label={t('legacy')} size="small" style={{ marginLeft: 8 }} />
                  )}
                </TableCell>
                <TableCell>{apiKey.keyPrefix}</TableCell>
                <TableCell>{apiKey.note}</TableCell>
                <TableCell>
                  <Chip 
                    label={t("apikey.status."+apiKey.status.toLowerCase())}
                    size="small" 
                    style={{ 
                      backgroundColor: 
                        apiKey.status === 'ACTIVE' ? '#11E0A0' :
                        apiKey.status === 'REVOKED' ? '#B83B86' :
                        apiKey.status === 'EXPIRED' ? '#DDAF3B' :
                        undefined,
                      color: '#fff'
                    }} 
                  />
                </TableCell>
                <TableCell>{apiKey.createdAt ? new Date(apiKey.createdAt).toLocaleString() : ''}</TableCell>
                <TableCell>{apiKey.expiresAt ? new Date(apiKey.expiresAt).toLocaleString() : t('forever')}</TableCell>
                <TableCell>{apiKey.revokedAt ? new Date(apiKey.revokedAt).toLocaleString() : ''}</TableCell>
                <TableCell align="left">
                  <IconButton onClick={() => setRevokeApiKey(apiKey)}>
                    <Avatar className={classes.avatar}>
                      <Icon>blockOutlined</Icon>
                    </Avatar>
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {revokeApiKey && <RevokeApiKeyDialog apiKey={{name: revokeApiKey.name, prefix: revokeApiKey.keyPrefix}} onClose={() => setRevokeApiKey(null)} onDelete={() => handleRevokeKey(revokeApiKey)} open={true} />}
      {showCreateDialog && <CreateApiKeyDialog visible={showCreateDialog} onClose={close} />}
    </React.Fragment>
  );
}
